import setupAxios from './setup-axios';
import ahoy from './setup-ahoy';

const MEDIA_ITEM_FIELDS_DEFAULT =
  'id title language_id webp_small_poster small_poster webp_medium_poster medium_poster producer theme duration format_price group_id group_visible group_theme group_format_price group_title is_paid group_index group_count known_words_count words_count monetization_restrict_access'
    .split(' ');

export default function API(apiUrl, parentWindow) {
  const axiosConfig = {
    baseURL: apiUrl
  };

  const axios = setupAxios(axiosConfig);

  axios.interceptors.request.use((config) => {
    let { headers } = config;

    headers = {
      ...headers,
      'X-Requested-From': parentWindow.location
    };

    return {
      ...config,
      headers
    };
  });

  const api = {
    fetchMediaItems({
      levels = [],
      theme = null,
      order = null,
      searchValue = '',
      ids = [],
      producerId = null,
      fields = MEDIA_ITEM_FIELDS_DEFAULT,
      page = 1,
      perPage = 20,
      payerId = null,
      withPrice = false,
      sourceLanguage,
      translationLanguage,
      watcherId = null
    }) {
      const url = '/media_items';

      const params = {
        q: {
          level_in: levels,
          search_by_with_detected_language: [searchValue, sourceLanguage],
          id_in: ids,
          user_id_eq: producerId,
          language_code_eq: sourceLanguage
        },
        fields,
        page,
        per_page: perPage
      };

      if (order) {
        params.q.s = order;
      }

      if (theme) {
        params.q.theme_name_eq = theme;
      }

      if (payerId) {
        params.q.payments_payer_id_or_group_payments_payer_id_eq = payerId;
        params.q.payments_status_eq = 'COMPLETED';
      }

      if (withPrice) {
        params.q.price_cents_or_group_price_cents_not_null = true;
      }

      if (translationLanguage) {
        params.translation_language_code = translationLanguage;
        params.fields = [...params.fields, 'translation_mediaitem_id'];
      }

      if (watcherId) {
        params.q.watched_mediaitems_user_id_eq = watcherId;
        params.q.s = 'watched_mediaitems_watched_at desc';
      }

      return axios.get(url, { params }).then(({ data }) => data);
    },

    fetchMediaItem(mediaItemId, params) {
      const url = `/media_items/${mediaItemId}`;

      return axios.get(url, { params }).then(({ data }) => data);
    },

    fetchMediaItemsLanguages() {
      return axios.get('/media_items/languages')
        .then(({ data }) => data);
    },

    fetchProfileData(userId, { fields = [] } = {}) {
      const url = `/users/${userId}`;
      const params = { fields };
      return axios.get(url, { params }, { withCredentials: true }).then(({ data }) => data);
    },

    fetchProfilesData(params = {}) {
      return axios.get('/users/profiles', { params }).then(({ data }) => data);
    },

    fetchFeedbacks({
      page = 1,
      perPage = 20,
      order = 'created_at desc',
      kind = null,
      state = null
    }) {
      const url = '/feedbacks';

      const params = {
        q: {
          s: order
        },
        page,
        per_page: perPage
      };

      if (kind) {
        params.q.kind_eq = kind;
      }

      if (state) {
        params.q.state_eq = state;
      }

      return axios.get(url, { params }).then(({ data }) => data);
    },

    createFeedback(feedback, reCaptchaToken = null, clientVersion = null) {
      const url = '/feedbacks';
      return axios.post(
        url,
        { feedback, recaptcha_response: reCaptchaToken, client_version: clientVersion }
      ).then(({ data }) => data);
    },

    attachMediaToFeedback(feedbackId, attachment) {
      const formData = new FormData();
      formData.append('file', attachment);
      return axios.post(`/feedbacks/${feedbackId}/attachment`, formData)
        .then(({ data }) => data);
    },

    fetchFeedback(id) {
      const url = `/feedbacks/${id}`;
      return axios.get(url, { withCredentials: true }).then(({ data }) => data);
    },

    createComment(comment) {
      const url = '/comments';
      return axios.post(url, { comment }, { withCredentials: true }).then(({ data }) => data);
    },

    toggleFeedbackSubscription(feedbackId) {
      const url = `/feedbacks/${feedbackId}/toggle_subscription`;
      return axios.post(url, null, { withCredentials: true }).then(({ data }) => data);
    },

    checkFeedbackSubscription(feedbackId) {
      const url = `/feedbacks/${feedbackId}/check_subscription`;
      return axios.get(url, { withCredentials: true }).then(({ data }) => data);
    },

    removeAvatar() {
      return axios.delete('users/remove_avatar', { withCredentials: true })
        .then(({ data }) => data);
    },

    deleteAccount() {
      return axios.delete('users/delete_account', { withCredentials: true })
        .then(({ data }) => data);
    },

    toggleFollow(followeeId) {
      const params = {
        followee_id: followeeId
      };

      return axios.post('/users/toggle_follow', params, { withCredentials: true }).then(({ data }) => data);
    },

    updateProfile(profile) {
      return axios.put('/users', { profile }, { withCredentials: true }).then(({ data }) => data);
    },

    createProfileSocialAccount(profileSocialAccount) {
      return axios.post('/profile_social_accounts',
        { profile_social_account: profileSocialAccount }, { withCredentials: true }
      ).then(({ data }) => data);
    },

    removeProfileSocialAccount(id) {
      return axios.delete(`/profile_social_accounts/${id}`, { withCredentials: true }).then(({ data }) => data);
    },

    fetchNotifications() {
      return axios.get('/notifications', { withCredentials: true }).then(({ data }) => data);
    },

    updateProfileEmailNotificationSetting(params) {
      return axios.put('/profile_email_notification_setting', { profile_email_notification_setting: params }, { withCredentials: true })
        .then(({ data }) => data);
    },

    updateNotification(notificationId, params) {
      return axios.put(`/notifications/${notificationId}`, { notification: params }, { withCredentials: true })
        .then(({ data }) => data);
    },

    markAllNotificationsAsRead() {
      return axios.put('/notifications/mark_all_as_read', { withCredentials: true })
        .then(({ data }) => data);
    },

    fetchUnreadNotificationsCount() {
      return axios.get('/notifications/unread_count', { withCredentials: true }).then(({ data }) => data);
    },

    fetchSubscriptions() {
      return axios.get('/followees', { withCredentials: true }).then(({ data }) => data);
    },

    trackEvent(eventName, params = {}) {
      return new Promise((resolve) => {
        resolve(ahoy().track(eventName, params));
      });
    },

    fetchMediaItemsGroup(groupId, params = {}) {
      const url = `/groups/${groupId}`;
      return axios.get(url, { params }).then(({ data }) => data);
    },

    fetchGroupsGroupedByTheme({
      levels = [],
      order = null,
      searchValue = '',
      withPrice = false,
      payerId = null,
      watcherId = null,
      sourceLanguage,
      producerId,
      ids = [],
      page = 1,
      perPage = 10
    }) {
      const params = {
        q: {
          level_in: levels,
          id_in: ids,
          search_by_with_detected_language: [searchValue, sourceLanguage],
          mediaitems_user_id_eq: producerId,
          language_code_eq: sourceLanguage
        },
        page,
        per_page: perPage
      };

      if (order) {
        params.q.s = order;
      }

      if (withPrice) {
        params.q.price_cents_or_mediaitems_price_cents_not_null = true;
      }

      if (watcherId) {
        params.q.watched_mediaitems_user_id_eq = watcherId;
      }

      if (payerId) {
        params.q.payments_payer_id_or_mediaitems_payments_payer_id_eq = payerId;
        params.q.payments_status_eq = 'COMPLETED';
      }

      return axios.get('/groups/grouped_by_theme', { params }).then(({ data }) => data);
    },

    fetchGroupsContinueWatching({
      levels = [],
      theme = null,
      order = null,
      searchValue = '',
      withPrice = false,
      payerId = null,
      sourceLanguage,
      producerId,
      page = 1,
      perPage = 20
    }) {
      const params = {
        q: {
          level_in: levels,
          search_by_with_detected_language: [searchValue, sourceLanguage],
          mediaitems_user_id_eq: producerId,
          language_code_eq: sourceLanguage
        },
        page,
        per_page: perPage
      };

      if (order) {
        params.q.s = order;
      }

      if (theme) {
        params.q.theme_name_eq = theme;
      }

      if (payerId) {
        params.q.payments_payer_id_or_mediaitems_payments_payer_id_eq = payerId;
        params.q.payments_status_eq = 'COMPLETED';
      }

      if (withPrice) {
        params.q.price_cents_or_mediaitems_price_cents_not_null = true;
      }

      return axios.get('/groups/continue_watching', { params }).then(({ data }) => data);
    },

    fetchGroups({
      levels = [],
      theme = null,
      order = null,
      searchValue = '',
      withPrice = false,
      payerId = null,
      watcherId = null,
      sourceLanguage,
      producerId,
      ids = [],
      page = 1,
      perPage = 10
    }) {
      const params = {
        q: {
          level_in: levels,
          id_in: ids,
          search_by_with_detected_language: [searchValue, sourceLanguage],
          mediaitems_user_id_eq: producerId,
          language_code_eq: sourceLanguage
        },
        page,
        per_page: perPage
      };

      if (order) {
        params.q.s = order;
      }

      if (theme) {
        params.q.theme_name_eq = theme;
      }

      if (payerId) {
        params.q.payments_payer_id_or_mediaitems_payments_payer_id_eq = payerId;
        params.q.payments_status_eq = 'COMPLETED';
      }

      if (withPrice) {
        params.q.price_cents_or_mediaitems_price_cents_not_null = true;
      }

      if (watcherId) {
        params.q.watched_mediaitems_user_id_eq = watcherId;
      }

      return axios.get('/groups', { params }).then(({ data }) => data);
    },

    toggleGroupSubscription(groupId) {
      const url = `/groups/${groupId}/toggle_subscription`;
      return axios.post(url, null, { withCredentials: true }).then(({ data }) => data);
    },

    upVote(resource, id) {
      return axios.put(`${apiUrl}/votes/${id}/up`, { resource })
        .then(({ data }) => data);
    },

    downVote(resource, id, reason = null) {
      return axios.put(`${apiUrl}/votes/${id}/down`, { resource, reason })
        .then(({ data }) => data);
    },

    removeVote(resource, id) {
      return axios.put(`${apiUrl}/votes/${id}/remove`, { resource }).then(({ data }) => data);
    },

    fetchBlogPosts(params) {
      return axios.get('/blog_posts', { params }).then(({ data }) => data);
    },

    fetchBlogPost(id) {
      return axios.get(`/blog_posts/${id}`).then(({ data }) => data);
    },

    fetchBlogCategories() {
      return axios.get('/blog_posts/categories').then(({ data }) => data);
    },

    fetchThemes() {
      const url = '/themes';
      return axios.get(url).then(({ data }) => data.themes);
    },

    fetchCurrentServerVersion() {
      const url = '/versions/current';
      return axios.get(url).then(({ data }) => data.version);
    },

    fetchFolders(userId, roles = [], fields = []) {
      const url = '/folders/tree';
      const params = {
        user_id: userId,
        roles,
        fields
      };

      return axios.get(url, { params, withCredentials: true })
        .then(({ data }) => data);
    },

    fetchMediaItemFoldersTree(userId, roles = [], fields = []) {
      const url = '/folders/mediaitem_folders_tree';
      const params = {
        user_id: userId,
        roles,
        fields
      };

      return axios.get(url, { params, withCredentials: true })
        .then(({ data }) => data);
    },

    fetchGroupFoldersTree(userId, roles = [], fields = []) {
      const url = '/folders/group_folders_tree';
      const params = {
        user_id: userId,
        roles,
        fields
      };

      return axios.get(url, { params, withCredentials: true })
        .then(({ data }) => data);
    },

    createFolder(name, parentId = null, fields = null) {
      const params = { name, parent_id: parentId, fields };

      return axios.post('/folders', params, { withCredentials: true })
        .then(({ data }) => data);
    },

    saveFolder(id) {
      const url = `/folders/${id}/save`;

      return axios.post(url, null, { withCredentials: true })
        .then(({ data }) => data);
    },

    deleteFolder(id) {
      const url = `/folders/${id}`;

      return axios.delete(url, { withCredentials: true })
        .then(({ data }) => data);
    },

    removeFolder(id) {
      const url = `/folders/${id}/remove`;

      return axios.delete(url, { withCredentials: true })
        .then(({ data }) => data);
    },

    updateFolder(id, params) {
      const url = `/folders/${id}`;

      return axios.put(url, params, { withCredentials: true })
        .then(({ data }) => data);
    },

    addMediaItemToFolder(folderId, mediaItemId) {
      const url = `/folders/${folderId}/add_media_item`;
      const params = { media_item_id: mediaItemId };

      return axios.post(url, params, { withCredentials: true })
        .then(({ data }) => data);
    },

    addGroupToFolder(folderId, groupId) {
      const url = `/folders/${folderId}/add_group`;
      const params = { group_id: groupId };

      return axios.post(url, params, { withCredentials: true })
        .then(({ data }) => data);
    },

    removeMediaItemFromFolder(folderId, mediaItemId) {
      const url = `/folders/${folderId}/remove_media_item`;
      const params = { media_item_id: mediaItemId };

      return axios.delete(url, { data: params }, { withCredentials: true })
        .then(({ data }) => data);
    },

    removeGroupFromFolder(folderId, groupId) {
      const url = `/folders/${folderId}/remove_group`;
      const params = { group_id: groupId };

      return axios.delete(url, { data: params }, { withCredentials: true })
        .then(({ data }) => data);
    }
  };

  return api;
}
